import TableSorter from './TableSorter';
import {objectAtPath} from "../../index";

// in a group of sorting fields, this contains information about the field being sorted: field, data type, direction
export default class TableSorterField {
	/**
	 * a column in a table is sorted by an array of fields in descending priority order.
	 * the first field in the list is sorted and if a record matches on this field, then the next field is used
	 *
	 * don't ever make field a function. it may be tempting, but, instead, preload the record with a new field
	 * with the desired value. this way each time the sort is performed it doesn't have to recreate the field which
	 * will perform much better.
	 *
	 * A table will sort ascending or descending. the direction of the field can be ascending or descending. This allows
	 * some fields to sort one direction and others to sort a different direction in the same sort group.
	 * ie effective date is descending but name is ascending, and when the table flips the sort order then effective date
	 * becomes ascending and name is descending.
	 *
	 * @param field - field in the object from which to get the data to sort
	 * @param sortType - uses TableSorter.SORT_... constants to define the type of data
	 * @param direction - uses TableSorter.DIRECTION_... constants to define the order of this field - note that table sort direction is different
	 * @param customSortFunction - can provide a custom sorter
	 */
	constructor(field, sortType, direction, customSortFunction) {
		this.field = field;
		this.sortType = sortType;
		this.direction = direction;
		this.customSortFunction = customSortFunction;

		// preload the sort function
		this.sorter = this.sortFunction();

		// == validation == //
		if (!this.field) {
			console.error('TableSorterField: field is required', this);
		}

		switch (this.direction) {
			case TableSorter.DIRECTION.DESCENDING:
			case TableSorter.DIRECTION.ASCENDING:
				break;
			default:
				console.error('TableSorterField: Unknown sort direction for field', this);
				break;
		}

		switch (this.sortType) {
			case TableSorter.SORT.DATE:
			case TableSorter.SORT.NUMBER:
			case TableSorter.SORT.TEXT:
			case TableSorter.SORT.FLOAT:
			case TableSorter.SORT.CUSTOM:
				break;
			default:
				console.error('TableSorterField: Unknown sort type for field', this);
				break;
		}


	}

	equals(tableSorterField) {
		return this === tableSorterField || (this.field === tableSorterField.field && this.sortType === tableSorterField.sortType && this.direction === tableSorterField.direction);
	}

	getField(o, defaultValue) {
		const value = objectAtPath(o, this.field);
		return value === undefined ? defaultValue : value;
	}

	sortFunction() {
		let defaultValue;
		let valueFunction;
		let compareFunction;
		switch (this.sortType) {
			case TableSorter.SORT.TEXT:
				valueFunction = TableSorterField.sorterFunctions.stringValue;
				defaultValue = '';
				compareFunction = (a, b) => a === b ? 0 : a === null ? -1 : a.localeCompare(b);
				break;
			case TableSorter.SORT.FLOAT:
				valueFunction = TableSorterField.sorterFunctions.floatValue;
				defaultValue = null;
				compareFunction = (a, b) => a === b ? 0 : a === null ? -1 : b === null ? 1 : a - b;
				break;
			case TableSorter.SORT.NUMBER:
				valueFunction = TableSorterField.sorterFunctions.intValue;
				defaultValue = null;
				compareFunction = (a, b) => a === b ? 0 : a === null ? -1 : b === null ? 1 : a - b;
				break;
			case TableSorter.SORT.DATE:
				valueFunction = TableSorterField.sorterFunctions.dateValue;
				defaultValue = false;
				compareFunction = (a, b) => a === b ? 0 : a === null ? -1 : b === null ? 1 : a - b;
				break;
			case TableSorter.SORT.CUSTOM:
				valueFunction = v => v;
				defaultValue = undefined;
				compareFunction = this.customSortFunction;
				break;
			default:
				throw `Unknown Sort Type: ${this.sortType}`;
		}

		return (a, b, sortDirection, tableSorter, tableSorterInformation) => {
			return compareFunction(
				valueFunction(this.getField(a, defaultValue)),
				valueFunction(this.getField(b, defaultValue)),
				a,
				b,
				sortDirection,
				tableSorter,
				tableSorterInformation
			);
		}
	}
}

TableSorterField.sorterFunctions = {
	floatValue: v => {
		//  note: for the sort, null is not the same as 0.0
		if (v === null) {
			return null
		} else {
			const result = parseFloat(v);
			// maintemplate.js sets 0 if can't parse float so doing same here
			return result ? result : 0;
		}
	},
	intValue: v => {
		//  note: for the sort, null is not the same as 0.0
		if (v === null) {
			return null
		} else {
			const result = parseInt(v, 10);
			// maintemplate.js sets 0 if can't parse int so doing same here
			return result ? result : 0;
		}
	},
	dateValue: v => {
		const result = v ? new Date(v).getTime() : 0;

		// maintemplate.js sets 0 if can't parse date so doing same here
		return result ? result : 0;
	},

	stringValue: v => {
		return typeof v === 'string' ? v.trim() : '';
	},
};
